import React from 'react';
import { UNSAFE_PortalProvider as AriaPortalProvider } from 'react-aria';
import { createPortal } from 'react-dom';

import { tv } from '../../tv';

/**
 * What every surfaced overlay shares (overlays.md): the portal it lives in and
 * the surface it wears. Internal to ComplyKit, imported directly, never
 * through the barrel.
 */

/* The surface every surfaced overlay wears, Toast included: the surface-overlay
 * fill, a border-subtle edge, the one overlay shadow and the step-3 radius. */
export const surfacedSurface =
  'ck:surface-overlay ck:border ck:border-border-subtle ck:shadow-overlay ck:rounded-3';

/* The surface plus the anchored motion pair, its axis picked by the placement
 * the popup actually resolved to. Menu and Popover spread it into their own
 * recipes; Toast has no trigger to slide from and keeps its own entrance. */
export const surfacedOverlay = [
  surfacedSurface,
  'ck:motion-overlay ck:exiting:motion-overlay-out',
  'ck:entering:opacity-0 ck:exiting:opacity-0',
  'ck:placement-top:entering:overlay-shift-down ck:placement-top:exiting:overlay-shift-down',
  'ck:placement-bottom:entering:overlay-shift-up ck:placement-bottom:exiting:overlay-shift-up',
  'ck:placement-left:entering:overlay-shift-right ck:placement-left:exiting:overlay-shift-right',
  'ck:placement-right:entering:overlay-shift-left ck:placement-right:exiting:overlay-shift-left',
];

/* Fixed, origin-aligned, full-width and zero-height: positioning never
 * depends on the body, which a scroll-locked Chakra dialog clips to its own
 * height with overflow hidden. */
export const portalStyles = tv({
  base: 'ck:fixed ck:top-0 ck:left-0 ck:w-full ck:h-0 ck:z-overlay',
});

/**
 * Tracks whether the popup is still in the DOM, so the portal outlives the
 * popup's exit transition. Returns the ref to put on the popup (it also
 * feeds the component's forwarded ref) and whether the portal should render.
 */
export function useOverlayPresence<T extends HTMLElement>(
  isOpen: boolean | undefined,
  forwardedRef: React.ForwardedRef<T>,
) {
  const [isPopupMounted, setPopupMounted] = React.useState(false);
  const popupRef = React.useCallback(
    (node: T | null) => {
      setPopupMounted(node !== null);
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );
  return { popupRef, isPresent: Boolean(isOpen) || isPopupMounted };
}

/**
 * The overlay's own portal container, mounted only while the overlay is
 * present: a parent Chakra dialog hides every body child from assistive
 * technology when it opens, so a container that existed before the overlay
 * opened would be hidden with the rest.
 */
export function OverlayPortal({ name, children }: { name: string; children: React.ReactNode }) {
  const [container, setContainer] = React.useState<HTMLDivElement | null>(null);
  if (typeof document === 'undefined') return null;
  return (
    <>
      {createPortal(
        /* A Chakra dialog's focus lock pulls focus out of any portal outside it. */
        <div
          ref={setContainer}
          className={portalStyles()}
          data-component={`${name}-portal`}
          data-no-focus-lock="true"
        />,
        document.body,
      )}
      {container && (
        <AriaPortalProvider getContainer={() => container}>{children}</AriaPortalProvider>
      )}
    </>
  );
}
